/**
 * CustomerPricingPanel — Sprint 2 (DEC-047 · BVA-A).
 *
 * Answers one operational question the moment a customer calls:
 *
 *     "What price should I charge this customer right now?"
 *
 * No mental arithmetic. Rack + Discount + Selling Price + Pricing
 * Confidence rendered live per product, per customer.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CurrencyDollar, Pencil, ArrowsClockwise, ClockCounterClockwise, Warning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const cpApi = {
  pricing:  (cid)       => api.get(`/customer-pricing/customer/${cid}/pricing`),
  setDiscount: (cid, code, body) => api.put(`/customer-pricing/discounts/${cid}/${code}`, body),
  history:  (cid, code) => api.get(`/customer-pricing/discounts/${cid}/${code}/history`),
};

const CONF_STYLES = {
  high:     { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "High confidence" },
  moderate: { chip: "bg-amber-100  text-amber-900   border-amber-300",    label: "Moderate confidence" },
  low:      { chip: "bg-rose-100   text-rose-800    border-rose-300",     label: "Low confidence" },
};

const MARGIN_STATUS_STYLES = {
  healthy: { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Healthy" },
  watch:   { chip: "bg-amber-100 text-amber-900 border-amber-300",       label: "Watch" },
  low:     { chip: "bg-rose-100 text-rose-800 border-rose-300",          label: "Low" },
  unknown: { chip: "bg-slate-100 text-slate-600 border-slate-300",       label: "Unknown" },
};

const isValidDec = (s) => /^\d+(\.\d{1,6})?$/.test((s ?? "").toString().trim());

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function CustomerPricingPanel({ customerId, canWrite }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);       // {code, current}
  const [historyFor, setHistoryFor] = useState(null); // code
  const [expandedMargin, setExpandedMargin] = useState({}); // {code: bool}

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data } = await cpApi.pricing(customerId);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pricing");
    } finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const missingRack = items.filter((i) => !i.rack.price_per_litre);

  return (
    <div className="space-y-3" data-testid="customer-pricing-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-600">
          Live rack from the Daily Pricing Engine (Sprint 1) minus this customer's discount, computed on every load.
          <span className="ml-1 text-slate-500">No calculator required.</span>
        </p>
        <button
          type="button"
          onClick={load}
          data-testid="customer-pricing-refresh"
          className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {missingRack.length > 0 ? (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-start gap-1.5" data-testid="customer-pricing-no-rack">
          <Warning size={14} weight="fill" className="mt-0.5" />
          <span>
            {missingRack.length === items.length
              ? "No selling prices have been posted yet. Selling prices cannot be computed until today's selling prices are published on the Daily Pricing screen."
              : `${missingRack.length} product(s) have no rack posted yet — showing '—' below.`}
          </span>
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Product</th>
                <th className="px-3 py-2 text-right font-medium">Rack</th>
                <th className="px-3 py-2 text-right font-medium">Discount</th>
                <th className="px-3 py-2 text-right font-medium">Selling</th>
                <th className="px-3 py-2 text-left font-medium">Margin</th>
                <th className="px-3 py-2 text-left font-medium">Effective</th>
                <th className="px-3 py-2 text-left font-medium">Confidence</th>
                <th className="px-3 py-2"/>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const conf = CONF_STYLES[it.pricing_confidence?.tier] || CONF_STYLES.moderate;
                const margin = it.margin_summary || { status: "unknown" };
                const mstyle = MARGIN_STATUS_STYLES[margin.status] || MARGIN_STATUS_STYLES.unknown;
                const isOpen = !!expandedMargin[it.product_code];
                return (
                  <Fragment key={it.product_code}>
                    <tr className="border-t border-slate-100" data-testid={`cp-row-${it.product_code}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{it.product_label}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid={`cp-rack-${it.product_code}`}>{it.rack.price_per_litre || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid={`cp-discount-${it.product_code}`}>{it.discount.discount_amount || <span className="text-slate-400">none</span>}</td>
                      <td className="px-3 py-2 text-right font-bold text-slate-900 tabular-nums" data-testid={`cp-selling-${it.product_code}`}>{it.selling_price || "—"}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setExpandedMargin((m) => ({ ...m, [it.product_code]: !m[it.product_code] }))}
                          data-testid={`cp-margin-toggle-${it.product_code}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${mstyle.chip} hover:opacity-80`}
                          title={margin.margin_pct ? `${margin.margin_pct}% margin` : (margin.reason || "Margin unknown")}
                        >
                          <span data-testid={`cp-margin-status-${it.product_code}`}>{mstyle.label}</span>
                          {margin.margin_pct ? <span className="opacity-70 tabular-nums">· {margin.margin_pct}%</span> : null}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{fmtDT(it.rack.effective_at)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${conf.chip}`}
                          title={(it.pricing_confidence?.reasons || []).join(" · ")}
                          data-testid={`cp-confidence-${it.product_code}`}
                        >
                          {conf.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setHistoryFor(it.product_code)}
                          data-testid={`cp-history-${it.product_code}`}
                          className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                          title="Show discount history"
                        >
                          <ClockCounterClockwise size={12} /> History
                        </button>
                        {canWrite ? (
                          <button
                            type="button"
                            onClick={() => setEditing({ code: it.product_code, label: it.product_label, current: it.discount.discount_amount || "" })}
                            data-testid={`cp-edit-${it.product_code}`}
                            className="ml-3 text-xs text-slate-800 hover:text-slate-950 font-semibold inline-flex items-center gap-1"
                          >
                            <Pencil size={12} /> Discount
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="bg-slate-50/70 border-t border-slate-100" data-testid={`cp-margin-detail-${it.product_code}`}>
                        <td colSpan={8} className="px-4 py-3">
                          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                            Margin Summary · Is this customer being priced appropriately today?
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                            <div>
                              <div className="text-xs text-slate-500">Selling Price</div>
                              <div className="font-semibold text-slate-900 tabular-nums" data-testid={`cp-margin-selling-${it.product_code}`}>
                                {margin.selling_price ? `$${margin.selling_price}` : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500">FIFO Next-Out Cost</div>
                              <div className="font-semibold text-slate-900 tabular-nums" data-testid={`cp-margin-cost-${it.product_code}`}>
                                {margin.next_out_cost ? `$${margin.next_out_cost}` : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500">Gross Margin / L</div>
                              <div className="font-semibold text-slate-900 tabular-nums" data-testid={`cp-margin-gross-${it.product_code}`}>
                                {margin.gross_margin_per_litre ? `$${margin.gross_margin_per_litre}` : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500">Margin %</div>
                              <div className="font-semibold text-slate-900 tabular-nums" data-testid={`cp-margin-pct-${it.product_code}`}>
                                {margin.margin_pct ? `${margin.margin_pct}%` : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500">Status</div>
                              <div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${mstyle.chip}`}>
                                  {mstyle.label}
                                </span>
                              </div>
                            </div>
                          </div>
                          {margin.reason ? (
                            <p className="text-xs text-slate-500 mt-2">{margin.reason}</p>
                          ) : null}
                          {margin.thresholds?.healthy_min_pct ? (
                            <p className="text-[11px] text-slate-500 mt-2">
                              Thresholds for {it.product_label} · Healthy ≥ {margin.thresholds.healthy_min_pct}% · Watch ≥ {margin.thresholds.watch_min_pct}% · Low below
                            </p>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <DiscountEditor
          customerId={customerId}
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      ) : null}
      {historyFor ? (
        <DiscountHistory
          customerId={customerId}
          productCode={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      ) : null}
    </div>
  );
}

function DiscountEditor({ customerId, product, onClose, onSaved }) {
  const [amount, setAmount] = useState(product.current || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const disabled = saving || !isValidDec(amount);

  const save = async () => {
    setSaving(true);
    try {
      await cpApi.setDiscount(customerId, product.code, { discount_amount: amount, reason: reason || null });
      toast.success(`Discount saved for ${product.label}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save discount");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center" onClick={onClose} data-testid="cp-edit-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="cp-edit-modal">
        <div>
          <div className="text-xs text-slate-500 uppercase">{product.code}</div>
          <h2 className="text-lg font-semibold text-slate-900">Set customer discount — {product.label}</h2>
          <p className="text-xs text-slate-600 mt-1">
            CAD per litre. This is the amount subtracted from today's selling price when quoting this product to this customer.
          </p>
        </div>
        <label className="text-sm block">
          <span className="block text-slate-600 mb-1">Discount amount (per litre)</span>
          <div className="flex items-center gap-1">
            <span className="text-slate-400 text-xs">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.0850"
              data-testid="cp-edit-amount"
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono tabular-nums"
            />
          </div>
        </label>
        <label className="text-sm block">
          <span className="block text-slate-600 mb-1">Reason (optional)</span>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Annual volume discount effective July 1."
            data-testid="cp-edit-reason"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} data-testid="cp-edit-cancel"
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={save} disabled={disabled} data-testid="cp-edit-save"
                  className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400">
            {saving ? "Saving…" : "Save discount"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DiscountHistory({ customerId, productCode, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    cpApi.history(customerId, productCode).then(({ data }) => {
      if (!cancelled) setRows(data.items || []);
    }).catch(() => toast.error("Failed to load history")).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [customerId, productCode]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center" onClick={onClose} data-testid="cp-history-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="cp-history-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Discount history — {productCode}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No discount history for this product.</p>
        ) : (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
            {rows.map((h) => (
              <li key={h.id} className="rounded border border-slate-200 p-3 text-sm">
                <div className="text-xs text-slate-500">{fmtDT(h.changed_at)} · {h.changed_by}</div>
                <div className="mt-1 text-slate-800">
                  {h.previous ?? <em className="text-slate-500">none</em>} → <b>{h.new}</b>
                </div>
                {h.reason ? <div className="mt-1 text-slate-600 text-xs">Reason: {h.reason}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
